import React, { useState } from "react";
import KycCdbFormModal from "./KycCdbFormModal";
import { CDB_FORM_META, cdbEntryName } from "../../../utils/cdbForms";
import "../css/KycCdbFormsPanel.css";

// CDB 2020 forms returned by the Swiss ownership API (docs/cdb-forms-rules.md),
// shown in the "UBOs Identification" modal for Switzerland-based organisations.

const SECTION_LABELS = {
  beneficialOwners: "Beneficial owners",
  controllingPersons: "Controlling persons",
  settlors: "Settlor(s)",
  founders: "Founder(s)",
  beneficiaries: "Beneficiaries",
  protectors: "Protector(s)",
  furtherPersons: "Further persons",
};

const HIDDEN_SECTIONS = new Set(["previousSettlors", "previousFounders"]);

function subjectLine(form, forms) {
  if (form.primary && form.subjectId === "X0") return `Contracting partner: ${form.contractingPartner}`;
  if (form.primary) {
    return form.contractingPartner
      ? `${form.subjectName} (Underlying company : ${form.contractingPartner})`
      : form.subjectName;
  }
  const parent = forms.find((f) => f.id === form.spawnedBy);
  return `${form.subjectName}${parent ? ` · required by Form ${parent.form}` : ""}`;
}

export default function KycCdbFormsPanel({ result }) {
  const forms = Array.isArray(result?.forms) ? result.forms : [];
  const [openFormId, setOpenFormId] = useState(null);
  // One combined note for all forms; a note shared by several forms appears once.
  const notes = [...new Map(forms.flatMap((f) => f.notes || []).map((n) => [n.code, n])).values()];
  if (!forms.length) {
    const info = (result?.errors || []).find((e) => ["QUOTED_ACCOUNT_HOLDER", "INDIVIDUAL_ACCOUNT_HOLDER"].includes(e.code));
    return <div className="kycReviewUboEmpty">{info?.msg || "No CDB form could be determined for this ownership structure."}</div>;
  }

  return (
    <div className="kycCdbForms">
      {forms.map((form) => {
        const meta = CDB_FORM_META[form.form] || {};
        // Form T further persons are not recorded in myKyc (always N/A on the form): not listed here.
        const sections = Object.entries(form.sections || {}).filter(([key]) =>
          !HIDDEN_SECTIONS.has(key) && !(form.form === "T" && key === "furtherPersons"));
        return (
          <div key={form.id} className="kycCdbFormCard">
            <div className="kycCdbFormHead">
              <div className="kycCdbFormLetter" aria-hidden="true">{form.form}</div>
              <div className="kycCdbFormIdentity">
                <div className="kycReviewUboName">Form {form.form} · {meta.title}</div>
                <div className="kycReviewUboMeta">{subjectLine(form, forms)}</div>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary kycCdbFormOpen" onClick={() => setOpenFormId(form.id)}>
                <i className="bi bi-box-arrow-up-right me-1" aria-hidden="true" />Open form
              </button>
            </div>

            <div className="kycCdbFormSections">
              {sections.map(([key, list]) => (
                <div key={key} className="kycCdbFormSection">
                  <div className="kycCdbFormSectionTitle">{SECTION_LABELS[key] || key}</div>
                  {form.form === "T" && key === "protectors" && !list.length ? (
                    <div className="kycCdbFormEmpty">No protector</div>
                  ) : list.length ? (
                    <ul className="kycCdbFormNames">
                      {list.map((entry, i) => (
                        <li key={`${entry.nodeId || entry.partyRef || entry.name}-${i}`}>
                          {cdbEntryName(entry, forms)}
                          {entry.kind === "entity" ? <span className="kycCdbFormTag">entity</span> : null}
                        </li>
                      ))}
                    </ul>
                  ) : key === "beneficiaries" && form.beneficiaryClass ? (
                    <div className="kycCdbFormEmpty">Class: {form.beneficiaryClass}</div>
                  ) : (
                    <div className="kycCdbFormEmpty">
                      {key === "controllingPersons" && form.controlBoxes?.includes(3)
                        ? "Highest managing director to be named"
                        : "None recorded"}
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        );
      })}
      {notes.length ? (
        <details className="kycCdbFormNotes">
          <summary>CDB 2020 notes</summary>
          {notes.map((note) => <p key={note.code}>{note.text}</p>)}
        </details>
      ) : null}
      <KycCdbFormModal forms={forms} formId={openFormId} onClose={() => setOpenFormId(null)} />
    </div>
  );
}
